import {DatePicker, Form, Modal, Row, Col, Card, Checkbox, Pagination} from "antd";
import {useFormik} from "formik";
import moment from "moment";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useStore} from "../../../../hooks/useStore";
import {ListItem, ListItemContainer} from "../../ShiftSuggestionStyles";
import {CompanyFilter} from "../../../_defaultComponents/FilterComponents";
import locale from "antd/es/date-picker/locale/tr_TR";

const {Meta} = Card;

const SendNotification = ({date, visibility, onCancel, setModalVisible}) => {
    const {t} = useTranslation(["shiftSuggestion"]);
    const stores = useStore();
    const [courierList, setCourierList] = useState([]);

    const [companies, setCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [sendNotificationCouriers, setSendNotificationCouriers] = useState([]);
    const [modalWidth, setModalWidth] = useState(520);

    const list =
        selectedCompanies.length > 0
            ? selectedCompanies.flatMap(
                  company =>
                      courierList?.find(
                          item => `${item.companyName} - ${item.city.capitalize()}` === company
                      )?.couriers ?? []
              )
            : courierList?.flatMap(item => item.couriers);

    const itemsPerPage = 50;
    const [currentPage, setCurrentPage] = useState(1);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedList = list?.slice(startIndex, endIndex);

    useEffect(() => {
        if (selectedCompanies.length > 0) {
            setSendNotificationCouriers(
                selectedCompanies.flatMap(
                    company =>
                        courierList
                            ?.find(
                                item =>
                                    `${item.companyName} - ${item.city.capitalize()}` === company
                            )
                            ?.couriers.map(item => item.oneSignalId) ?? []
                )
            );
        } else {
            setSendNotificationCouriers(
                courierList?.flatMap(item => item.couriers).map(item => item.oneSignalId)
            );
        }
    }, [selectedCompanies]);

    useEffect(() => {
        if (sendNotificationCouriers?.length > 0) {
            setModalWidth(1000);
        } else {
            setModalWidth(520);
        }
    }, [sendNotificationCouriers?.length]);
    const formik = useFormik({
        initialValues: {
            date: ""
        },
        validationSchema: yup.object().shape({
            date: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        enableReinitialize: true,
        onSubmit: () => {}
    });

    const {values, errors, touched, setFieldValue} = formik;

    const getDisabledDate = current => {
        let customDate = moment().format("DD.MM.YYYY");
        return current && current < moment(customDate, "DD.MM.YYYY");
    };

    const handleSelectDate = async date => {
        setFieldValue("date", date);
        const response = await stores.shiftSuggestionStore.getCouriersToChooseShifts({
            date: moment(date).format("YYYY-MM-DD")
        });
        setCompanies(
            response?.data?.result.map(item => `${item.companyName} - ${item.city.capitalize()}`)
        );
        setCourierList(response?.data?.result);
        setSendNotificationCouriers(
            response?.data?.result?.flatMap(item => item.couriers)?.map(item => item.oneSignalId)
        );
        setSelectedCompanies(
            response?.data?.result.map(item => `${item.companyName} - ${item.city.capitalize()}`)
        );
    };

    const onOk = async () => {
        try {
            stores.appStore.openConfirmModal({
                title: t("modal.confirmTitle", {ns: "common"}),
                content: t("couriersWillBeNotified", {
                    ns: "shiftSuggestion",
                    count: sendNotificationCouriers.length
                }),
                onOk: async () => {
                    formik.handleSubmit();
                    const preferenceResponse = await stores.shiftSuggestionStore.sendPreferenceNotification(
                        {
                            date: moment(date).format("YYYY-MM-DD"),
                            oneSignalIds: sendNotificationCouriers
                        }
                    );
                    if (preferenceResponse?.status === 200) {
                        let message = t("couriersNotified", {
                            ns: "shiftSuggestion",
                            count:
                                preferenceResponse?.data?.result?.recipients ||
                                sendNotificationCouriers.length
                        });
                        if (preferenceResponse?.data?.result?.errors) {
                            if (
                                preferenceResponse?.data?.result?.errors?.invalid_player_ids
                                    ?.length > 0
                            ) {
                                const couriers = courierList?.flatMap(item => item.couriers);
                                let notSend = couriers.filter(item =>
                                    preferenceResponse?.data?.result?.errors?.invalid_player_ids.includes(
                                        item.oneSignalId
                                    )
                                );
                                if (notSend.length > 0) {
                                    message += `\n\n${t("notificationCouldntBeSentToCouriers", {
                                        ns: "shiftSuggestion",
                                        count: notSend.length
                                    })}\n${notSend?.map(item => `- ${item.fullname}`).join("\n")}`;
                                }
                                const notificationDetailNotFound =
                                    sendNotificationCouriers?.length -
                                    preferenceResponse?.data?.result.recipients -
                                    notSend.length;
                                if (notificationDetailNotFound) {
                                    message += `\n${t("courierNotificationDetailNotFound", {
                                        ns: "shiftSuggestion",
                                        count:
                                            sendNotificationCouriers?.length -
                                            preferenceResponse?.data?.result.recipients -
                                            notSend
                                    })}`;
                                }
                            }
                        }
                        stores.appStore.openSuccessModal({
                            title: t("modal.successfulTitle", {ns: "common"}),
                            content: message
                        });
                    } else {
                    }
                    setModalVisible(false);
                    setFieldValue("date", "");
                    setCourierList([]);
                }
            });
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Modal
            centered
            width={modalWidth}
            title={t("sendNotificationModalTitle", {ns: "shiftSuggestion"})}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
                setFieldValue("date", "");
                setCourierList([]);
            }}
            okButtonProps={{
                disabled: values.date === "" || courierList.couriers?.length == 0
            }}
            onOk={onOk}
            okText={t("modal.sendButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            label={t("fields.date", {ns: "common"})}
                            validateStatus={errors.date && touched.date ? "error" : "success"}
                            help={errors.date && touched.date ? errors.date : null}
                        >
                            <DatePicker
                                locale={locale}
                                value={values.date}
                                format={"DD.MM.YYYY"}
                                disabledDate={getDisabledDate}
                                onSelect={date => handleSelectDate(date)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            validateStatus={errors.date && touched.date ? "error" : "success"}
                            help={errors.date && touched.date ? errors.date : null}
                        >
                            {courierList.length > 0 && (
                                <>
                                    <CompanyFilter
                                        mode="multiple"
                                        showPoolOption={false}
                                        allKey="all"
                                        value={selectedCompanies}
                                        list={companies}
                                        onChange={value => {
                                            setSelectedCompanies(value);
                                        }}
                                    />
                                    <Row gutter={[16, 16]}>
                                        {paginatedList?.map(item => (
                                            <ListItemContainer
                                                key={item.fullname}
                                                className="gutter-row"
                                                span={6}
                                            >
                                                <ListItem>
                                                    <Meta
                                                        avatar={
                                                            <Checkbox
                                                                onChange={() =>
                                                                    setSendNotificationCouriers(
                                                                        prev => {
                                                                            if (
                                                                                prev.includes(
                                                                                    item.oneSignalId
                                                                                )
                                                                            ) {
                                                                                return prev.filter(
                                                                                    id =>
                                                                                        id !==
                                                                                        item.oneSignalId
                                                                                );
                                                                            }
                                                                            return [
                                                                                ...prev,
                                                                                item.oneSignalId
                                                                            ];
                                                                        }
                                                                    )
                                                                }
                                                                checked={sendNotificationCouriers?.includes(
                                                                    item.oneSignalId
                                                                )}
                                                            />
                                                        }
                                                        title={item.fullname}
                                                    />
                                                </ListItem>
                                            </ListItemContainer>
                                        ))}
                                    </Row>
                                    <Pagination
                                        style={{
                                            marginTop: 20
                                        }}
                                        onChange={value => setCurrentPage(value)}
                                        defaultCurrent={1}
                                        total={Math.floor((list?.length * 10) / itemsPerPage)}
                                    />
                                </>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default SendNotification;
